/**
 * @file Vista Component Mask Layer — Tag Transformation
 *
 * @description
 * This module is the single import point for all Vista (wdpr-*) web components
 * used in the Syndicated Nav application. It acts as a "mask layer" that
 * re-registers every Vista custom element under a prefixed tag name
 * (nav-wdpr-*) and exports React wrappers pointing to those prefixed tags.
 *
 * @why
 * Marketplace also uses Vista web components on the same page. Without
 * prefixing, both applications would register `wdpr-*` custom elements on the
 * same `customElements` registry, causing collisions and unpredictable
 * rendering. By transforming our tags to `nav-wdpr-*`, both apps coexist
 * without conflicts.
 *
 * @how
 * 1. `setTagTransformer(fn)` — called BEFORE any `defineCustomElement()`. It
 *    tells the Vista library to use `nav-wdpr-*` as the registered tag name
 *    instead of the default `wdpr-*`.
 * 2. `defineCustomElement()` — registers each component on the global
 *    `customElements` registry under its transformed name.
 * 3. `createComponent()` from `@lit/react` — creates React wrapper components
 *    that render the custom element by its prefixed `tagName` and forward
 *    props/events correctly.
 *
 * @adding-a-new-component
 * 1. Import the class + `defineCustomElement` from the component's path in
 *    `@wdpr/ra-web-components/dist/components/wdpr-<name>.js`
 * 2. Call `defineWdpr<Name>()` in the registration block (after setTagTransformer).
 * 3. Export a React wrapper via `createComponent({ tagName: "nav-wdpr-<name>", ... })`.
 * 4. Add the component name to the `expectedComponents` array in `vista.test.js`.
 * 5. Update SCSS selectors to target `nav-wdpr-<name>` (not `wdpr-<name>`).
 *
 * @limitations
 * - Components that perform internal `querySelector` or `tagName` checks using
 *   the original `wdpr-*` name may not work correctly after transformation.
 *   Known example: `wdpr-card-xlarge` internally queries for child elements by
 *   their un-prefixed tag name; verify behavior when adding card components.
 * - The tag transformer is global — once set, ALL subsequent `defineCustomElement`
 *   calls in the same bundle will use the transformer.
 * - SCSS selectors MUST target `nav-wdpr-*` (not `wdpr-*`) for styling to apply.
 *
 * @see https://jira.disney.com/browse/ROS-120791
 */

import React from "react";
import { createComponent } from "@lit/react";
import { setTagTransformer } from "@wdpr/ra-web-components/dist/components/index.js";
import {
	WdprAvatar as $WdprAvatar,
	defineCustomElement as defineWdprAvatar,
} from "@wdpr/ra-web-components/dist/components/wdpr-avatar.js";
import {
	WdprBottomNavList as $WdprBottomNavList,
	defineCustomElement as defineWdprBottomNavList,
} from "@wdpr/ra-web-components/dist/components/wdpr-bottom-nav-list.js";
import {
	WdprButton as $WdprButton,
	defineCustomElement as defineWdprButton,
} from "@wdpr/ra-web-components/dist/components/wdpr-button.js";
import {
	WdprCardXlarge as $WdprCardXlarge,
	defineCustomElement as defineWdprCardXlarge,
} from "@wdpr/ra-web-components/dist/components/wdpr-card-xlarge.js";
import {
	WdprCardXlargeContent as $WdprCardXlargeContent,
	defineCustomElement as defineWdprCardXlargeContent,
} from "@wdpr/ra-web-components/dist/components/wdpr-card-xlarge-content.js";
import {
	WdprDivider as $WdprDivider,
	defineCustomElement as defineWdprDivider,
} from "@wdpr/ra-web-components/dist/components/wdpr-divider.js";
import {
	WdprIconButton as $WdprIconButton,
	defineCustomElement as defineWdprIconButton,
} from "@wdpr/ra-web-components/dist/components/wdpr-icon-button.js";
import {
	WdprIconLibrary as $WdprIconLibrary,
	defineCustomElement as defineWdprIconLibrary,
} from "@wdpr/ra-web-components/dist/components/wdpr-icon-library.js";
import {
	WdprIcon as $WdprIcon,
	defineCustomElement as defineWdprIcon,
} from "@wdpr/ra-web-components/dist/components/wdpr-icon.js";
import {
	WdprMedia as $WdprMedia,
	defineCustomElement as defineWdprMedia,
} from "@wdpr/ra-web-components/dist/components/wdpr-media.js";
import {
	WdprNavIconButton as $WdprNavIconButton,
	defineCustomElement as defineWdprNavIconButton,
} from "@wdpr/ra-web-components/dist/components/wdpr-nav-icon-button.js";
import {
	WdprNavItemLarge as $WdprNavItemLarge,
	defineCustomElement as defineWdprNavItemLarge,
} from "@wdpr/ra-web-components/dist/components/wdpr-nav-item-large.js";
import {
	WdprNavItemMedium as $WdprNavItemMedium,
	defineCustomElement as defineWdprNavItemMedium,
} from "@wdpr/ra-web-components/dist/components/wdpr-nav-item-medium.js";
import {
	WdprNavItemSmall as $WdprNavItemSmall,
	defineCustomElement as defineWdprNavItemSmall,
} from "@wdpr/ra-web-components/dist/components/wdpr-nav-item-small.js";
import {
	WdprNavLoginButton as $WdprNavLoginButton,
	defineCustomElement as defineWdprNavLoginButton,
} from "@wdpr/ra-web-components/dist/components/wdpr-nav-login-button.js";
import {
	WdprNavMenuButton as $WdprNavMenuButton,
	defineCustomElement as defineWdprNavMenuButton,
} from "@wdpr/ra-web-components/dist/components/wdpr-nav-menu-button.js";
import {
	WdprNotificationIndicator as $WdprNotificationIndicator,
	defineCustomElement as defineWdprNotificationIndicator,
} from "@wdpr/ra-web-components/dist/components/wdpr-notification-indicator.js";
import {
	WdprPageHeader as $WdprPageHeader,
	defineCustomElement as defineWdprPageHeader,
} from "@wdpr/ra-web-components/dist/components/wdpr-page-header.js";
import {
	WdprScrollbar as $WdprScrollbar,
	defineCustomElement as defineWdprScrollbar,
} from "@wdpr/ra-web-components/dist/components/wdpr-scrollbar.js";
import {
	WdprSearchInput as $WdprSearchInput,
	defineCustomElement as defineWdprSearchInput,
} from "@wdpr/ra-web-components/dist/components/wdpr-search-input.js";
import {
	WdprSecondaryNavSection as $WdprSecondaryNavSection,
	defineCustomElement as defineWdprSecondaryNavSection,
} from "@wdpr/ra-web-components/dist/components/wdpr-secondary-nav-section.js";
import {
	WdprTextLink as $WdprTextLink,
	defineCustomElement as defineWdprTextLink,
} from "@wdpr/ra-web-components/dist/components/wdpr-text-link.js";
import {
	WdprBadge as $WdprBadge,
	defineCustomElement as defineWdprBadge,
} from "@wdpr/ra-web-components/dist/components/wdpr-badge.js";

// ─── Tag Transformation ─────────────────────────────────────────────────────
// Must be called BEFORE any defineCustomElement() to register components
// under the prefixed tag names (nav-wdpr-*), avoiding collision with
// Marketplace's wdpr-* components on the same page.
//
// ─────────────────────────────────────────────────────────────────────────────
const EXCLUDED_TAGS = new Set([]);

setTagTransformer((tag) => {
	if (tag.startsWith("wdpr-") && !EXCLUDED_TAGS.has(tag)) {
		return tag.replace("wdpr-", "nav-wdpr-");
	}
	return tag;
});

// ─── Register All Components (under nav-wdpr-* names) ───────────────────────
defineWdprAvatar();
defineWdprBottomNavList();
defineWdprButton();
defineWdprCardXlarge();
defineWdprCardXlargeContent();
defineWdprDivider();
defineWdprIconButton();
defineWdprIconLibrary();
defineWdprIcon();
defineWdprMedia();
defineWdprNavIconButton();
defineWdprNavItemLarge();
defineWdprNavItemMedium();
defineWdprNavItemSmall();
defineWdprNavLoginButton();
defineWdprNavMenuButton();
defineWdprNotificationIndicator();
defineWdprPageHeader();
defineWdprScrollbar();
defineWdprSearchInput();
defineWdprSecondaryNavSection();
defineWdprTextLink();
defineWdprBadge();

// ─── React Wrappers (using transformed tag names) ───────────────────────────
export const WdprAvatar = createComponent({
	tagName: "nav-wdpr-avatar",
	elementClass: $WdprAvatar,
	react: React,
	events: { onWdprToggle: "wdprToggle" },
});

export const WdprBottomNavList = createComponent({
	tagName: "nav-wdpr-bottom-nav-list",
	elementClass: $WdprBottomNavList,
	react: React,
	events: { onWdprBottomNavListClick: "wdprBottomNavListClick" },
});

export const WdprButton = createComponent({
	tagName: "nav-wdpr-button",
	elementClass: $WdprButton,
	react: React,
	events: {
		onWdprClick: "wdprClick",
		onWdprDisabledChange: "wdprDisabledChange",
		onWdprExitComplete: "wdprExitComplete",
	},
});

export const WdprCardXlarge = createComponent({
	tagName: "nav-wdpr-card-xlarge",
	elementClass: $WdprCardXlarge,
	react: React,
	events: { onWdprCardClick: "wdprCardClick" },
});

export const WdprCardXlargeContent = createComponent({
	tagName: "nav-wdpr-card-xlarge-content",
	elementClass: $WdprCardXlargeContent,
	react: React,
	events: {},
});

export const WdprDivider = createComponent({
	tagName: "nav-wdpr-divider",
	elementClass: $WdprDivider,
	react: React,
	events: {},
});

export const WdprIconButton = createComponent({
	tagName: "nav-wdpr-icon-button",
	elementClass: $WdprIconButton,
	react: React,
	events: { onClicked: "clicked" },
});

export const WdprIconLibrary = createComponent({
	tagName: "nav-wdpr-icon-library",
	elementClass: $WdprIconLibrary,
	react: React,
	events: {},
});

export const WdprIcon = createComponent({
	tagName: "nav-wdpr-icon",
	elementClass: $WdprIcon,
	react: React,
	events: {},
});

export const WdprMedia = createComponent({
	tagName: "nav-wdpr-media",
	elementClass: $WdprMedia,
	react: React,
	events: {},
});

export const WdprNavIconButton = createComponent({
	tagName: "nav-wdpr-nav-icon-button",
	elementClass: $WdprNavIconButton,
	react: React,
	events: {
		onWdprNavIconButtonClick: "wdprNavIconButtonClick",
		onWdprNavIconButtonHover: "wdprNavIconButtonHover",
		onWdprNavIconButtonFocusIn: "wdprNavIconButtonFocusIn",
		onWdprNavIconButtonPressed: "wdprNavIconButtonPressed",
	},
});

export const WdprNavItemLarge = createComponent({
	tagName: "nav-wdpr-nav-item-large",
	elementClass: $WdprNavItemLarge,
	react: React,
	events: { onWdprNavItemLargeClick: "wdprNavItemLargeClick" },
});

export const WdprNavItemMedium = createComponent({
	tagName: "nav-wdpr-nav-item-medium",
	elementClass: $WdprNavItemMedium,
	react: React,
	events: {
		onWdprNavItemMediumClick: "wdprNavItemMediumClick",
		onWdprNavItemMediumExitComplete: "wdprNavItemMediumExitComplete",
	},
});

export const WdprNavItemSmall = createComponent({
	tagName: "nav-wdpr-nav-item-small",
	elementClass: $WdprNavItemSmall,
	react: React,
	events: {
		onWdprNavItemSmallClick: "wdprNavItemSmallClick",
		onWdprNavItemSmallExitComplete: "wdprNavItemSmallExitComplete",
	},
});

export const WdprNavLoginButton = createComponent({
	tagName: "nav-wdpr-nav-login-button",
	elementClass: $WdprNavLoginButton,
	react: React,
	events: {
		onWdprNavLoginClick: "wdprNavLoginClick",
		onWdprNavLoginHover: "wdprNavLoginHover",
		onWdprNavLoginFocusIn: "wdprNavLoginFocusIn",
		onWdprNavLoginPressed: "wdprNavLoginPressed",
	},
});

export const WdprNavMenuButton = createComponent({
	tagName: "nav-wdpr-nav-menu-button",
	elementClass: $WdprNavMenuButton,
	react: React,
	events: {
		onWdprNavMenuButtonClick: "wdprNavMenuButtonClick",
		onWdprNavMenuButtonHover: "wdprNavMenuButtonHover",
		onWdprNavMenuButtonFocusIn: "wdprNavMenuButtonFocusIn",
		onWdprNavMenuButtonPressed: "wdprNavMenuButtonPressed",
	},
});

export const WdprNotificationIndicator = createComponent({
	tagName: "nav-wdpr-notification-indicator",
	elementClass: $WdprNotificationIndicator,
	react: React,
	events: {},
});

export const WdprPageHeader = createComponent({
	tagName: "nav-wdpr-page-header",
	elementClass: $WdprPageHeader,
	react: React,
	events: {
		onLeadingClick: "leadingClick",
		onTrailingClick: "trailingClick",
		onWdprPageHeaderExitComplete: "wdprPageHeaderExitComplete",
	},
});

export const WdprScrollbar = createComponent({
	tagName: "nav-wdpr-scrollbar",
	elementClass: $WdprScrollbar,
	react: React,
	events: {
		onScrollChange: "scrollChange",
		onPositionInitialized: "positionInitialized",
	},
});

export const WdprSearchInput = createComponent({
	tagName: "nav-wdpr-search-input",
	elementClass: $WdprSearchInput,
	react: React,
	events: {
		onInputBlur: "inputBlur",
		onInputClick: "inputClick",
		onInputFocus: "inputFocus",
		onLeadingClick: "leadingClick",
		onTrailingClick: "trailingClick",
		onValueChanged: "valueChanged",
	},
});

export const WdprSecondaryNavSection = createComponent({
	tagName: "nav-wdpr-secondary-nav-section",
	elementClass: $WdprSecondaryNavSection,
	react: React,
	events: { onWdprSecondaryNavSectionClick: "wdprSecondaryNavSectionClick" },
});

export const WdprTextLink = createComponent({
	tagName: "nav-wdpr-text-link",
	elementClass: $WdprTextLink,
	react: React,
	events: {},
});

export const WdprBadge = createComponent({
	tagName: "nav-wdpr-badge",
	elementClass: $WdprBadge,
	react: React,
	events: {},
});
